import type { ThemePreference } from '@erp/shared';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn.ts';
import { useTheme } from '../../lib/theme.ts';

const THEMES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Oscuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
];

/**
 * Claro / Oscuro / Sistema as native radio buttons (keyboard and screen readers for
 * free). What a choice does is up to the caller: the user menu also saves it to the
 * account; the access screens only apply it to this browser.
 */
export function ThemePicker({
  onChange,
  className,
  children,
}: {
  onChange: (theme: ThemePreference) => void;
  className?: string;
  /** Below the options (e.g. a note during an impersonation). */
  children?: ReactNode;
}) {
  const id = useId();
  const theme = useTheme();
  return (
    <fieldset className={className}>
      <legend className="pb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Tema
      </legend>
      <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
        {THEMES.map(({ value, label, icon: Icon }) => (
          <label
            key={value}
            className={cn(
              'flex min-h-10 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-md text-xs has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-ring',
              theme === value && 'bg-surface font-semibold shadow-sm',
            )}
          >
            <input
              type="radio"
              name={`${id}-tema`}
              value={value}
              className="sr-only"
              checked={theme === value}
              onChange={() => {
                onChange(value);
              }}
            />
            <Icon className="size-4" aria-hidden="true" />
            {label}
          </label>
        ))}
      </div>
      {children}
    </fieldset>
  );
}
